<template>
  <div>
    <div class="layout__position-left layout__width-100">
      <el-form
        :model="searchParams"
        status-icon
        :rules="{}"
        ref="searchParams"
      >

        <div class="layout__padding-clear text__align-center layout__width-100">
          <div class="layout__search-field__insights-8">
            <p class="layout__margin__bottom-10 text__align-left">Group by</p>
            <el-form-item prop="groupBy">
              <el-select
                v-model="searchParams.groupBy"
                filterable
                placeholder="Territory"
                :clearable="false"
                @change="updateSearchParams"
              >
                <el-option
                  v-for="reportType in reportTypes"
                  :key="reportType.value"
                  :label="reportType.name"
                  :value="reportType.value"
                >
                </el-option>
              </el-select>
            </el-form-item>
          </div>

          <div
            v-if="isMonthReport"
            class="layout__search-field__insights-8"
          >
            <p class="layout__margin__bottom-10 text__align-left">Month</p>
            <el-form-item prop="fromDate">
              <el-date-picker
                v-model="searchParams.fromDate"
                type="month"
                :clearable="false"
                format="MMMM yyyy"
                value-format="yyyy-MM-dd"
                placeholder="Month"
                @change="updateSearchParams"
              />
            </el-form-item>
          </div>
          <div
            v-if="isDayReport"
            class="layout__search-field__insights-8 layout__width-250px"
          >
            <p class="layout__margin__bottom-10 text__align-left">Days</p>
            <el-form-item prop="fromDate">
              <el-date-picker
                v-model="searchParams.daysRange"
                type="daterange"
                align="left"
                :clearable="false"
                start-placeholder="Start Date"
                end-placeholder="End Date"
                :picker-options="pickerOptions"
                format="dd/MM/yyyy"
                value-format="yyyy-MM-dd"
                @change="updateSearchParams"
              />
            </el-form-item>
          </div>
          <div
            class="layout__search-field__insights-8"
          >
            <p class="layout__margin__bottom-10 text__align-left">Branch*</p>
            <el-form-item prop="branch">
              <el-input
                placeholder="Branch"
                clearable
                v-model="searchParams.branch"
                @clear="updateSearchParams"
                @input="onBranchInput"
              />
            </el-form-item>
          </div>
        </div>
      </el-form>

      <el-button
        class="button-red button__base button__branches"
        :disabled="! searchParams.branch"
        @click="updateSearchParams"
      >
        Filter branch
      </el-button>

      <el-button
        class="button-teal button__base button__insights"
        :disabled="this.sales.length === 0"
        @click="csv"
      >
        Download CSV
      </el-button>
    </div>

    <div class="layout__margin__bottom-10 pl-10">
      * To filter by branch enter the branch code or just click on the desired Id in the table.
    </div>
  </div>
</template>

<script>
import Axios from 'axios';
import { axios } from '@/mixins/Axios.js';
import { csvDownloadHelper } from '@/mixins/CsvDownloadHelper.js';
import { dataFormatter } from '@/mixins/DataFormatter.js';
import AbandonedOrders from '../../enums/AbandonedOrders';
import moment from 'moment';

export default {
  name: 'AbandonedOrdersSearch',

  mixins: [axios, csvDownloadHelper, dataFormatter],

  props: {
    searchParams: {},

    sales: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      isLoading: false,
      serviceRouteUrl: `${process.env.VUE_APP_INSIGHTSAPIHOST}`,
      exportDate: new Date().toLocaleDateString('en-GB') + '_abandoned_orders.csv',
      AbandonedOrders: AbandonedOrders,
      downloadUrl: '',
      reportTypes: [
        {
          name: 'By month',
          value: 'MONTH',
        },
        {
          name: 'By days',
          value: 'DAY',
        },
      ],

      pickerOptions: {
        shortcuts: [{
          text: 'This month',
          onClick(picker) {
            picker.$emit('pick', [new Date(), new Date()]);
          },
        }, {
          text: 'This year',
          onClick(picker) {
            const end = new Date();
            const start = new Date(new Date().getFullYear(), 0);
            picker.$emit('pick', [start, end]);
          },
        }, {
          text: 'Last 6 months',
          onClick(picker) {
            const end = new Date();
            const start = new Date();
            start.setMonth(start.getMonth() - 6);
            picker.$emit('pick', [start, end]);
          },
        }],
      },
    };
  },

  watch: {
    currentPage() {
      this.getSales();
    },

    total() {
      this.getSales();
    },
  },

  computed: {
    currentPage() {
      return this.searchParams.currentPage;
    },

    total() {
      return this.searchParams.total;
    },

    isMonthReport() {
      return this.searchParams.groupBy === 'MONTH';
    },

    isDayReport() {
      return this.searchParams.groupBy === 'DAY';
    },
  },

  mounted() {
    this.getSales();
  },

  methods: {
    prepareParams(getCount = 0) {
      const params = {};

      params.groupBy = this.searchParams.groupBy;

      if (getCount === 1) {
        params.getCount = '1';
        params.start = '0';
        params.cnt = '0';
      } else {
        params.getCount = '0';
        params.start = this.searchParams.pagination * (this.searchParams.currentPage - 1);
        params.cnt = this.searchParams.pagination;
      }

      if (this.isMonthReport) {
        params.fromDate = this.searchParams.fromDate;
        params.toDate = moment(params.fromDate).endOf('month').format('yyyy-MM-DD');
      }

      if (this.isDayReport) {
        params.fromDate = this.searchParams.daysRange[0];
        params.toDate = this.searchParams.daysRange[1];
      }

      if (this.searchParams.branch) {
        params.branch = this.searchParams.branch;
      }

      return new URLSearchParams(params).toString();
    },

    getSales() {
      if (this.isLoading) {
        return;
      }

      this.isLoading = true;

      const endpoint = `${this.serviceRouteUrl}/api/v1/DynamicQuery/GetAbandonedOrders?${this.prepareParams()}`;

      this.$parent.loading = true;

      Axios.get(endpoint, this.returnHeaderUsingToken())
        .then(response => {
          let abandonedOrders = response.data?.length ? response.data : [];

          abandonedOrders = abandonedOrders.map((abandonedOrder) => {
            abandonedOrder['OrderDate'] = abandonedOrder['OrderDate']
              ? moment(abandonedOrder['OrderDate']).format('DD/MM/YYYY')
              : '';

            abandonedOrder['OrderMonth'] = abandonedOrder['OrderMonth']
              ? moment(abandonedOrder['OrderMonth'], 'M').format('MMMM')
              : '';

            return {
              ...abandonedOrder,
            };
          });

          this.$parent.sales = abandonedOrders;

          this.$nextTick(() => { // Loading should be closed asynchronously
            this.$parent.loading = false;
          });

          this.isLoading = false;
        })
        .catch(error => {
          if (error.response) {
            this.$message.error('Sorry there has been an issue retrieving the information you requested.');
          }

          this.$nextTick(() => { // Loading should be closed asynchronously
            this.$parent.loading = false;
          });

          this.$parent.sales = [];

          this.isLoading = false;
        });
    },

    csv() {
      const fields = this.AbandonedOrders.createLabelValueList();

      this.downloadCsv(this.exportDate, fields, this.sales);
    },

    updateSearchParams() {
      this.$emit('update-search-params');
    },

    onBranchInput() {
      if (! this.searchParams.branch) {
        this.$emit('update-search-params');
      }
    },
  },
};
</script>